
import { Component, Vue, Watch } from 'vue-property-decorator';
import { TableHeaderConfig } from '@/views/interfaces/vuetify/table.interface';
import { Roles } from '@/services/Roles';
import HasScope from '@/components/Mixins/HasScope';
import DepartmentRepositoryInterface, { DepartmentInterface } from '@/services/Departments/Interfaces';
import DeleteDepartmentDialog from '@/components/dialogs/departments/DeleteDepartmentDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    HasScope,
  ],
  components: {
    DeleteDepartmentDialog,
    PageHeader,
  },
  inject: [
    'departmentRepository',
  ],
})
export default class AllDepartments extends Vue {
  private departmentRepository!: DepartmentRepositoryInterface

  private loading: boolean = true;
  private headers: TableHeaderConfig[] = [
    { text: 'ID', value: '_id' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];
  private createDepartmentScope: string = Roles.USER_DEPARTMENTS_CREATE;
  private search: string = '';
  private departments: DepartmentInterface[] = [];
  private deleteDepartmentForDialog: DepartmentInterface | false = false;
  private deleteDepartmentDialog: string = '';
  private options: ListPaginationOptions = defaultListOptions();
  private readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private created(): void {
    this.applyRouteQuery();
    this.initialize();
  }

  private applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  private async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'department_index',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  private async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  private onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'department_index') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  private async initialize(): Promise<void> {
    this.departments = await this.departmentRepository.getAllDepartments();
    this.loading = false;
  }

  private editDepartment(department: DepartmentInterface): void {
    this.$router.push({ name: 'department_edit', params: { departmentId: department._id } });
  }

  private deleteDepartment(department: DepartmentInterface): void {
    this.deleteDepartmentDialog = 'delete-department-dialog';
    this.deleteDepartmentForDialog = department;
  }

  private createDepartment(): void {
    this.$router.push({ name: 'department_create' });
  }

  private filterDepartments(departmentId: string): void {
    this.departments = this.departments.filter((department) => {
      return department._id !== departmentId;
    });
  }
}
